import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

// ============================================
// 自定义 URL 校验器（严格检查尾部斜杠）
// ============================================

/**
 * URL 校验器：禁止尾部斜杠
 */
function urlSchema(description: string) {
  return z.string().min(1).refine(
    (url) => {
      if (url.endsWith('/')) return false;
      try {
        new URL(url);
        return true;
      } catch {
        return false;
      }
    },
    `${description} must be a valid URL without trailing slash`
  );
}

/**
 * URL 或相对路径校验器：支持完整 URL 或以 / 开头的相对路径
 */
function urlOrPathSchema(description: string) {
  return z.string().min(1).refine(
    (value) => {
      // 允许单独的 /（根路径）
      if (value === '/') return true;
      // 其他情况禁止尾部斜杠
      if (value.endsWith('/')) return false;
      // 允许相对路径（以 / 开头）
      if (value.startsWith('/')) return true;
      // 验证完整 URL
      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
    },
    `${description} must be a valid URL or absolute path (starting with /) without trailing slash`
  );
}
const now = new Date();
const version = [
  now.getFullYear(),
  String(now.getMonth() + 1).padStart(2, '0'),
  String(now.getDate()).padStart(2, '0'),
].join('/');
const time = [
  String(now.getHours()).padStart(2, '0'),
  String(now.getMinutes()).padStart(2, '0'),
  String(now.getSeconds()).padStart(2, '0'),
].join(':');
const fullVersion = `${version} ${time}`;

// 校验环境变量，集中管理环境变量
export const Env = createEnv({
  server: {
    AUTH_SECRET: z.string().min(1),
    AUTH_TRUST_HOST: z.enum(['true']),
    AUTH_URL: urlSchema('AUTH_URL'),
    AUTH_GOOGLE_ID: z.string().min(1),
    AUTH_GOOGLE_SECRET: z.string().min(1),
    AUTH_FACEBOOK_ID: z.string().min(1),
    AUTH_FACEBOOK_SECRET: z.string().min(1),
    AUTH_TELEGRAM_BOT_TOKEN: z.string().min(1),
    RSA_PUBLIC_KEY: z.string().min(1),
    // Sentry Auth Token 默认使用 prod.env 中的值，可以通过环境变量覆盖
    SENTRY_AUTH_TOKEN: z.string().min(1).default('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'),
  },
  client: {
    NEXT_PUBLIC_ENV_NAME: z.string().min(1),
    NEXT_PUBLIC_HTTP_SERVER: urlOrPathSchema('NEXT_PUBLIC_HTTP_SERVER'),
    NEXT_PUBLIC_APP_VERSION: z.string(),
    NEXT_PUBLIC_REACT_QUERY_DEVTOOLS: z.enum(['true']).optional(),
    // 全局 Sentry 开关，默认关闭；设置为 'false' 可完全关闭前后端 Sentry
    NEXT_PUBLIC_ENABLE_SENTRY: z.enum(['true', 'false']).default('false'),
    NEXT_PUBLIC_TELEGRAM_BOT_USERNAME: z.string().min(1),
    NEXT_PUBLIC_TELEGRAM_BOT_ID: z.string().min(1),
    NEXT_PUBLIC_CLOUND_FLARE_KEY: z.string().min(1),
    NEXT_PUBLIC_SENTRY_ENVIRONMENT: z.enum(['development', 'test', 'pre', 'production']).default('development'),
    NEXT_PUBLIC_THINKINGDATA_APP_ID: z.string().min(1),
    NEXT_PUBLIC_THINKINGDATA_SERVER_URL: urlSchema('NEXT_PUBLIC_THINKINGDATA_SERVER_URL'),
    // CDN 备用域名列表，逗号分隔，为空时降级到源站
    NEXT_PUBLIC_CDN_FALLBACK_HOSTS: z.string().default(''),
    // doh 查询 DNS txt 记录的域名
    NEXT_PUBLIC_DOH_QUERY_HOST: z.string().min(1),
    // Icon/Image debug mode toggle
    NEXT_PUBLIC_DEBUG_ICONS: z.enum(['true', 'false']).optional(),
    // 品牌标识（分盘相关）- 使用 z.string() 而非 z.enum，避免 A 盘名出现在 B 盘产物中
    NEXT_PUBLIC_BRAND: z.string().min(1),
    NEXT_PUBLIC_APP_NAME: z.string().min(1),
    NEXT_PUBLIC_APP_DOMAIN: z.string().min(1),
    NEXT_PUBLIC_APP_SLOGAN: z.string().min(1),
    NEXT_PUBLIC_BRAND_ASSETS_BASE: z.string().min(1),
    NEXT_PUBLIC_USE_COME_DOT_STYLE: z.enum(['true', 'false']).default('true'),
    NEXT_PUBLIC_BRAND_ASSETS_VERSION: z.string().default('1'),
    NEXT_PUBLIC_APK_DOWNLOAD_HOST: z.string().default(''),
    NEXT_PUBLIC_RESPONSIBLE_GAMING_URL: z.string().default(''),
  },
  shared: {
    NODE_ENV: z.enum(['test', 'development', 'production']),
    ANALYZE: z.enum(['true']).optional(),
    START_MOCK_SERVER: z.enum(['true']).optional(),
    MOCK_SERVER_PORT: z.string().optional(),
    REGION: z.enum(['global', 'india', 'brazil']).default('global'),
    DEBUG: z.enum(['true', 'false']).default('false'),
    RELEASE: z.string().default('dev-local'),
    CDN_HOST: z.string().default(''),
  },
  // You need to destructure all the keys manually
  runtimeEnv: {
    AUTH_SECRET: process.env.AUTH_SECRET,
    AUTH_TRUST_HOST: process.env.AUTH_TRUST_HOST,
    AUTH_URL: process.env.AUTH_URL,
    AUTH_GOOGLE_ID: process.env.AUTH_GOOGLE_ID,
    AUTH_GOOGLE_SECRET: process.env.AUTH_GOOGLE_SECRET,
    AUTH_FACEBOOK_ID: process.env.AUTH_FACEBOOK_ID,
    AUTH_FACEBOOK_SECRET: process.env.AUTH_FACEBOOK_SECRET,
    AUTH_TELEGRAM_BOT_TOKEN: process.env.AUTH_TELEGRAM_BOT_TOKEN,
    RSA_PUBLIC_KEY: process.env.RSA_PUBLIC_KEY,
    SENTRY_AUTH_TOKEN: process.env.SENTRY_AUTH_TOKEN || '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',

    NEXT_PUBLIC_ENV_NAME: process.env.NEXT_PUBLIC_ENV_NAME,
    NEXT_PUBLIC_HTTP_SERVER: process.env.NEXT_PUBLIC_HTTP_SERVER,
    NEXT_PUBLIC_APP_VERSION: process.env.NODE_ENV !== 'development' ? fullVersion : '',
    NEXT_PUBLIC_ENABLE_SENTRY: process.env.NEXT_PUBLIC_ENABLE_SENTRY,
    NEXT_PUBLIC_TELEGRAM_BOT_USERNAME: process.env.NEXT_PUBLIC_TELEGRAM_BOT_USERNAME,
    NEXT_PUBLIC_TELEGRAM_BOT_ID: process.env.NEXT_PUBLIC_TELEGRAM_BOT_ID,
    NEXT_PUBLIC_REACT_QUERY_DEVTOOLS: process.env.NEXT_PUBLIC_REACT_QUERY_DEVTOOLS,
    NEXT_PUBLIC_CLOUND_FLARE_KEY: process.env.NEXT_PUBLIC_CLOUND_FLARE_KEY,
    NEXT_PUBLIC_SENTRY_ENVIRONMENT: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT,
    NEXT_PUBLIC_THINKINGDATA_APP_ID: process.env.NEXT_PUBLIC_THINKINGDATA_APP_ID,
    NEXT_PUBLIC_THINKINGDATA_SERVER_URL: process.env.NEXT_PUBLIC_THINKINGDATA_SERVER_URL,
    NEXT_PUBLIC_CDN_FALLBACK_HOSTS: process.env.NEXT_PUBLIC_CDN_FALLBACK_HOSTS,
    NEXT_PUBLIC_DOH_QUERY_HOST: process.env.NEXT_PUBLIC_DOH_QUERY_HOST,
    NEXT_PUBLIC_DEBUG_ICONS: process.env.NEXT_PUBLIC_DEBUG_ICONS,
    NEXT_PUBLIC_BRAND: process.env.NEXT_PUBLIC_BRAND,
    NEXT_PUBLIC_APP_NAME: process.env.NEXT_PUBLIC_APP_NAME,
    NEXT_PUBLIC_APP_DOMAIN: process.env.NEXT_PUBLIC_APP_DOMAIN,
    NEXT_PUBLIC_APP_SLOGAN: process.env.NEXT_PUBLIC_APP_SLOGAN,
    NEXT_PUBLIC_BRAND_ASSETS_BASE: process.env.NEXT_PUBLIC_BRAND_ASSETS_BASE,
    NEXT_PUBLIC_USE_COME_DOT_STYLE: process.env.NEXT_PUBLIC_USE_COME_DOT_STYLE,
    NEXT_PUBLIC_BRAND_ASSETS_VERSION: process.env.NEXT_PUBLIC_BRAND_ASSETS_VERSION,
    NODE_ENV: process.env.NODE_ENV,
    ANALYZE: process.env.ANALYZE,
    START_MOCK_SERVER: process.env.START_MOCK_SERVER,
    MOCK_SERVER_PORT: process.env.MOCK_SERVER_PORT,
    REGION: process.env.NEXT_PUBLIC_REGION,
    DEBUG: process.env.NEXT_PUBLIC_DEBUG,
    RELEASE: process.env.NEXT_PUBLIC_GIT_COMMIT,
    CDN_HOST: process.env.NEXT_PUBLIC_CDN_HOST,
    NEXT_PUBLIC_APK_DOWNLOAD_HOST: process.env.NEXT_PUBLIC_APK_DOWNLOAD_HOST,
    NEXT_PUBLIC_RESPONSIBLE_GAMING_URL: process.env.NEXT_PUBLIC_RESPONSIBLE_GAMING_URL,
  },
});
